<template>
  <v-dialog 
    v-model="showConfirmationDialogue" 
    max-width="500" 
    persistent
    overlay-opacity="0.7"
  >
    <v-card class="confirmation-dialog">
      <!-- Header with colored accent -->
      <div class="dialog-header" :class="`bg-${status}`">
        <div class="header-content">
          <v-icon class="header-icon white--text" size="24">{{ icon }}</v-icon>
          <span class="header-title white--text text-h6 font-weight-medium">
            {{ title }}
          </span>
        </div>
        <v-btn 
          icon 
          small 
          class="close-btn white--text" 
          @click="reject"
        >
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-content pa-4">
        <!-- Main content area -->
        <div class="content-section">
          <p class="text-body-1 text--primary mb-3" v-html="text"></p>

          <!-- Remarks section -->
          <div v-if="showRemarks" class="remarks-section mt-3">
            <TextareaField
              v-model="remarks"
              label="Remarks"
              placeholder="Enter your remarks here..."
              hide-details="auto"
              counter
              maxlength="255"
              auto-grow
              rows="2"
              outlined
              :rules="isRemarkRequired ? [(val) => !!val || 'Remarks is required'] : []"
              class="remarks-field"
            />
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Action buttons -->
      <v-card-actions class="dialog-actions pa-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          color="grey darken-1"
          @click="reject"
          class="action-btn mr-2"
        >
          {{ rejectButton }}
        </v-btn>
        <v-btn
          :color="status"
          :disabled="showRemarks && isRemarkRequired && !remarks"
          @click="confirmed"
          class="action-btn"
          elevation="2"
        >
          {{ confirmButton }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TextareaField from "@/components/FormBaseComponents/TextareaField.vue";

export default {
  name: 'ConfirmationDialog',
  components: {
    TextareaField,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: "mdi-help-circle",
    },
    status: {
      type: String,
      default: "primary",
      validator: function(value) {
        return ['primary', 'secondary', 'success', 'info', 'warning', 'error'].includes(value);
      }
    },
    title: {
      type: String,
      default: "Confirm Action",
    },
    text: {
      type: String,
      default: "Are you sure you want to confirm?",
    },
    confirmButton: {
      type: String,
      default: "Confirm",
    },
    rejectButton: {
      type: String,
      default: "Cancel",
    },
    showRemarks: {
      type: Boolean,
      default: false,
    },
    isRemarkRequired: {
      type: Boolean,
      default: false,
    },
    remarkValue: {
      type: String,
      default: "",
    },
  },
  data: function() {
    return {
      remarks: null,
    };
  },
  computed: {
    showConfirmationDialogue: {
      get: function() {
        return this.value;
      },
      set: function(value) {
        this.$emit("input", value);
      },
    },
  },
  watch: {
    showConfirmationDialogue: function(newValue) {
      if (newValue) {
        this.remarks = this.remarkValue;
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    confirmed: function() {
      var payload = {};
      
      if (this.showRemarks) {
        payload.remarks = this.remarks;
      }
      
      this.$emit("confirmed", payload);
      this.showConfirmationDialogue = false;
    },
    reject: function() {
      this.$emit("rejected");
      this.showConfirmationDialogue = false;
    },
    resetForm: function() {
      this.remarks = null;
    },
  },
};
</script>

<style scoped>
.confirmation-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-content {
  display: flex;
  align-items: center;
}

.header-icon {
  margin-right: 12px;
}

.header-title {
  margin: 0;
}

.close-btn {
  margin: -4px;
}

.dialog-content {
  background-color: #fafafa;
}

.content-section {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.remarks-section {
  margin-top: 12px;
}

.remarks-field {
  margin-top: 4px;
}

.dialog-actions {
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;
}

.action-btn {
  text-transform: none !important;
  font-weight: 500;
  min-width: 80px;
}

/* Status color variants */
.bg-primary {
  background-color: #1976d2 !important;
}

.bg-secondary {
  background-color: #424242 !important;
}

.bg-success {
  background-color: #4caf50 !important;
}

.bg-info {
  background-color: #2196f3 !important;
}

.bg-warning {
  background-color: #ff9800 !important;
}

.bg-error {
  background-color: #f44336 !important;
}

/* Animation for dialog entrance */
.v-dialog__content {
  animation: dialogSlideIn 0.3s ease-out;
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>